import { FormGroup } from "@cofano/cck";
import { Language, usePermission } from "utils";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { gql } from "__generated__/gql";
import { LocationLink, RelationLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { Box, IconButton, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { GeneralConnectingShipPortVisitFragment } from "__generated__/view/connectingVisits/graphql/connectingVisit.fragments.generated";

type ConnectingVisitInfoProps = {
	connectingVisit: Pick<GeneralConnectingShipPortVisitFragment, "id" | "from" | "until" | "agent" | "lastUpdateFromAgentOn">
	onDelete?: () => void;
	action: {
		id: number | null;
		connectingShipBerthing?: {
			location?: {
				id: number
				description: string;
			} | null;
			window?: {
				from?: number | null;
				to?: number | null;
			} | null;
		} | null;
		connectingVisitNotes?: string | null;
	};
};
const ConnectingVisitInfo = ({ connectingVisit, onDelete, action }: ConnectingVisitInfoProps) => {
	const { isRTR } = usePermission();

	const [ deleteVisit, { loading } ] = useMutation(DELETE_CONNECTING_VISIT_MUTATION);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", width: "fit-content", minWidth: 150 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
				<Typography variant="h3" sx={{ fontSize: 14, fontWeight: 600 }} color="textPrimary">
					{intl.formatMessage({ id: "SHIP_TO_SHIP" })}
				</Typography>

				{onDelete && action && (
					<IconButton size="small" onClick={() => !loading && action.id && deleteVisit({ variables: { cargoActionId: action.id } }).then(() => onDelete())}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				)}
			</Box>

			<FormGroup label={Language("CONNECTING_VISIT_PORT_STAY")} overflow="hidden">
				<FormattedDateWindow
					from={connectingVisit.from}
					to={connectingVisit.until}
					withoutYear />
			</FormGroup>

			<FormGroup label={Language("BERTH_LOCATION")} overflow="hidden">
				<LocationLink location={action?.connectingShipBerthing?.location} />
			</FormGroup>

			<FormGroup label={Language("BERTH_WINDOW")} overflow="hidden">
				<FormattedDateWindow
					from={action?.connectingShipBerthing?.window?.from}
					to={action?.connectingShipBerthing?.window?.to}
					withoutYear />
			</FormGroup>

			<FormGroup label={Language("AGENCY")} overflow="hidden">
				{connectingVisit.agent ? isRTR ? <RelationLink relation={connectingVisit.agent} /> : connectingVisit.agent.name : "-"}
			</FormGroup>

			<FormGroup label={Language("LAST_UPDATE_FROM_AGENT")} overflow="hidden">
				<FormattedDate date={connectingVisit.lastUpdateFromAgentOn} withoutYear />
			</FormGroup>

			<FormGroup label={Language("NOTE")} overflow="hidden">
				<Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "small" }}>
					{action?.connectingVisitNotes}
				</Typography>
			</FormGroup>
		</Box>
	);
};

const DELETE_CONNECTING_VISIT_MUTATION = gql(`#graphql
	mutation unlinkConnectingShipPortVisit($cargoActionId: Long!) {
		unlinkConnectingShipPortVisit(cargoActionId: $cargoActionId)
	}
`);

export default ConnectingVisitInfo;
